import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button, 
    CircularProgress, Typography, FormControlLabel, Checkbox, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const DepartmentPage = () => {
    useAuthCheck();
    const { departmentId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [department, setDepartment] = useState({});
    const initialValues = {
        departmentCode: department.departmentCode || '',
        description: department.description || '',
        level: department.level || 0,
        isActive: department.isActive !== undefined ? department.isActive : true,
    };
    

    useEffect(() => {     
        const fetchDepartment = async () => {
            try {
                setLoading(true);

                if (departmentId) {
                    const res = await api.get(`/departments/${departmentId}`);                    
                    setDepartment(res.data);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }; 
        
        fetchDepartment();
    }, [departmentId]); 
    
    const checkoutSchema = yup.object().shape({
        departmentCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = departmentId ? 'PUT' : 'POST';
            const url = departmentId 
                ? `/departments/update/${departmentId}` 
                : '/departments/new';

            await api({
                method,
                url,
                data: {
                    departmentCode: values.departmentCode,
                    description: values.description,
                    level: values.level,
                    isActive: values.isActive,
                },
            });

            toast.success(departmentId 
                ? 'Department updated successfully' 
                : 'Department created successfully'
            );
            
            // onSubmitProps.resetForm();
            navigate("/departments");
        } catch (error) {
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Department"
                    subtitle={`${loading ? 'Loading...' : departmentId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/departments")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Department Code"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.departmentCode}
                                        name="departmentCode"
                                        error={!!touched.departmentCode && !!errors.departmentCode}
                                        helperText={touched.departmentCode && errors.departmentCode}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        label="Level"
                                        name="level"
                                        type="number"
                                        fullWidth
                                        variant="filled"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.level}                                            
                                        slotProps={{
                                            input: {
                                                sx: {
                                                    "& input": { textAlign: "right" },
                                                },
                                            },
                                        }}
                                        sx={{ gridColumn: "span 12" }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 12' }}
                                        control={
                                            <Checkbox
                                                name="isActive"
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {departmentId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default DepartmentPage;
